import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { StyleShadowRoot } from "@redotech/react-util/style";
import { SignUpFormGoal } from "@redotech/redo-model/signup-form";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { isValidUrlToShowForm } from "@redotech/redo-web/marketing/signup-form/utils";
import { getFormsByGoal } from "@redotech/shopify-sdk/rpc/schema/forms/get-forms-by-goal";
import { memoizeAsyncV2 } from "@redotech/util/memoize";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RedoShopifyRpcClientContext } from "../../../client/redo-shopify-rpc-client";
import STYLE_MANAGER from "../../../style-manager";
import { getVariantIdFromUrl } from "../../../utils";
import { BackInStockFormModal } from "./back-in-stock-form-modal";
import { useBackInStockRoot } from "./use-back-in-stock-root";

/**
 * Reads the selected variant ID from the product form's hidden input.
 * Works on themes that don't update the URL on variant selection.
 */
function getVariantIdFromForm(): string | null {
  const form = document.querySelector<HTMLFormElement>(
    'form[action*="/cart/add"]',
  );
  if (!form) {
    return null;
  }
  const input = form.querySelector<HTMLInputElement>('[name="id"]');
  return input?.value || null;
}

function getSelectedVariantFromScript(): {
  id: string;
  available: boolean;
} | null {
  const script = document.querySelector("script[data-selected-variant]");
  if (!script?.textContent) {
    return null;
  }
  try {
    const data = JSON.parse(script.textContent);
    if (data && typeof data.id !== "undefined") {
      return { id: String(data.id), available: Boolean(data.available) };
    }
  } catch {
    return null;
  }
  return null;
}

function getSelectedOptionValues(): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  const radios = document.querySelectorAll<HTMLInputElement>(
    'fieldset input[type="radio"]:checked',
  );
  for (const radio of radios) {
    if (radio.value && radio.name && !seen.has(radio.name)) {
      seen.add(radio.name);
      values.push(radio.value);
    }
  }
  if (values.length > 0) {
    return values;
  }
  const selects = document.querySelectorAll<HTMLSelectElement>(
    "fieldset select, .product-form__input select",
  );
  for (const select of selects) {
    if (select.value && select.name && !seen.has(select.name)) {
      seen.add(select.name);
      values.push(select.value);
    }
  }
  return values;
}

function useVariantChangeDetection(callback: () => void) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    const debouncedCallback = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => callbackRef.current(), 150);
    };

    // Initial check
    callbackRef.current();

    // URL changes (pushState/replaceState/popstate)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      debouncedCallback();
    };
    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      debouncedCallback();
    };
    addEventListener("popstate", debouncedCallback);

    // Variant selector changes (radio buttons, selects)
    const handleChange = (e: Event) => {
      const target = e.target;
      if (
        !(target instanceof HTMLInputElement) &&
        !(target instanceof HTMLSelectElement)
      ) {
        return;
      }
      if (target instanceof HTMLInputElement && target.type !== "radio") {
        return;
      }
      debouncedCallback();
    };
    document.addEventListener("change", handleChange);

    return () => {
      clearTimeout(debounceTimer);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      removeEventListener("popstate", debouncedCallback);
      document.removeEventListener("change", handleChange);
    };
  }, []);
}

export function useVariantStockStatus(): {
  isOutOfStock: boolean;
  isCheckingStock: boolean;
  productId: string | null;
  variantId: string | null;
} {
  const [isOutOfStock, setIsOutOfStock] = useState<boolean>(false);
  const [isCheckingStock, setIsCheckingStock] = useState<boolean>(true);
  const [productId, setProductId] = useState<string | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);

  const fetchProductFromStorefront = useMemo(
    () =>
      memoizeAsyncV2(
        async (productHandle: string) => {
          const response = await fetch(`/products/${productHandle}.js`);
          if (!response.ok) {
            return undefined;
          }
          const product = await response.json();
          return product as {
            id: number;
            variants: { id: number; available: boolean; options: string[] }[];
          };
        },
        { cacheSize: 1, keyFn: (input) => input[0] },
      ),
    [],
  );

  const checkCurrentVariantStock = useCallback(async () => {
    try {
      setIsCheckingStock(true);

      const productHandle =
        location.pathname.match(/\/products\/([^/?]+)/)?.[1];

      if (!productHandle) {
        setIsOutOfStock(false);
        setIsCheckingStock(false);
        return;
      }

      const product = await fetchProductFromStorefront(productHandle);
      if (!product || !product.variants || product.variants.length === 0) {
        setIsOutOfStock(false);
        setIsCheckingStock(false);
        return;
      }

      const productId = String(product.id);

      // Match selected option values against variant options (most reliable — radios update immediately)
      const selectedOptions = getSelectedOptionValues();
      if (selectedOptions.length > 0) {
        const matchedVariant = product.variants.find((v) => {
          return (
            selectedOptions.length === v.options.length &&
            selectedOptions.every((opt, i) => opt === v.options[i])
          );
        });
        if (matchedVariant) {
          setIsOutOfStock(!matchedVariant.available);
          setIsCheckingStock(false);
          setProductId(productId);
          setVariantId(String(matchedVariant.id));
          return;
        }
      }

      // Try URL param or form hidden input
      const variantIdFromUrl =
        getVariantIdFromUrl(location.href) ?? getVariantIdFromForm();
      if (variantIdFromUrl) {
        const variant = product.variants.find(
          (v) => String(v.id) === variantIdFromUrl,
        );
        if (variant) {
          setIsOutOfStock(!variant.available);
          setIsCheckingStock(false);
          setProductId(productId);
          setVariantId(variantIdFromUrl);
          return;
        }
      }

      // Try script[data-selected-variant] (may be stale on some themes)
      const scriptVariant = getSelectedVariantFromScript();
      if (scriptVariant) {
        setIsOutOfStock(!scriptVariant.available);
        setIsCheckingStock(false);
        setProductId(productId);
        setVariantId(scriptVariant.id);
        return;
      }

      // Fallback: first available or first variant
      const firstVariant =
        product.variants.find((v) => v.available) ?? product.variants[0];

      if (firstVariant) {
        setIsOutOfStock(!firstVariant.available);
        setIsCheckingStock(false);
        setProductId(productId);
        setVariantId(String(firstVariant.id));
      } else {
        setIsOutOfStock(false);
        setIsCheckingStock(false);
      }
    } catch (error) {
      console.error("Error checking variant stock status:", error);
      setIsOutOfStock(false);
      setIsCheckingStock(false);
    }
  }, [fetchProductFromStorefront]);

  useVariantChangeDetection(checkCurrentVariantStock);

  return { isOutOfStock, isCheckingStock, productId, variantId };
}

const BackInStockContent = memo(function BackInStockContent({
  autocheck,
}: {
  autocheck: WidgetAutocheck;
}) {
  const rpcClient = useRequiredContext(RedoShopifyRpcClientContext);
  const { isOutOfStock, isCheckingStock, productId, variantId } =
    useVariantStockStatus();

  const shopifyCustomerId = useMemo(
    () =>
      (
        globalThis as any
      ).ShopifyAnalytics?.meta?.page?.customerId?.toString() ?? null,
    [],
  );

  useEffect(() => {
    if (isCheckingStock) {
      return;
    }
    if (isOutOfStock && productId && variantId) {
      document.dispatchEvent(
        new CustomEvent("redo:variant-unavailable", {
          detail: { productId, variantId },
        }),
      );
    } else {
      document.dispatchEvent(new CustomEvent("redo:variant-available"));
    }
  }, [isOutOfStock, isCheckingStock, productId, variantId]);

  // Load back in stock form
  const backInStockFormLoad = useLoad(async () => {
    if (!rpcClient) {
      return [];
    }

    const forms = await rpcClient(getFormsByGoal, {
      goal: SignUpFormGoal.BACK_IN_STOCK,
    });
    return forms.filter((f: (typeof forms)[number]) => f.publishedAt);
  }, [rpcClient]);

  const [backInStockForm, setBackInStockForm] = useState<
    { id: string; publishedAt: Date | null; form: SignupForm } | undefined
  >(undefined);

  useEffect(() => {
    if (backInStockFormLoad.value) {
      setBackInStockForm(backInStockFormLoad.value[0]);
    }
  }, [backInStockFormLoad.value]);

  if (
    isCheckingStock ||
    !backInStockForm ||
    !isOutOfStock ||
    !isValidUrlToShowForm(backInStockForm.form)
  ) {
    return null;
  }

  return (
    <BackInStockFormModal
      brandKit={autocheck.brandKit}
      client={rpcClient}
      form={backInStockForm.form}
      formId={backInStockForm.id}
      productId={productId}
      shopifyCustomerId={shopifyCustomerId}
      variantId={variantId}
    />
  );
});

export const ProductBackInStockStorefront = memo(
  function ProductBackInStockStorefront({
    autocheck,
  }: {
    autocheck: WidgetAutocheck;
  }) {
    const backInStockRoots = useBackInStockRoot(autocheck);

    if (backInStockRoots.length === 0) {
      return null;
    }

    return (
      <>
        {backInStockRoots.map(({ element }) => (
          <StyleShadowRoot
            key={element.id}
            shadowRoot={element.root}
            styleManager={STYLE_MANAGER}
          >
            <BackInStockContent autocheck={autocheck} />
          </StyleShadowRoot>
        ))}
      </>
    );
  },
);
